import useAuthentication from '@/modules/authentication/useAuthentication'
import useError from '@/modules/error/useError'
import helpers from '@/modules/message/helpers'
import { t } from '@/plugins/i18n.ts'
import useRequestManager from '@/store/useRequestManager'
import * as Sentry from '@sentry/vue'
import { AxiosError, HttpStatusCode } from 'axios'

type StorefrontApiError = {
  httpStatus: string
  timestamp: string
  detail: string
  message?: string
  requestId?: string
}

type ApiError = {
  code?: HttpStatusCode
  axiosError?: AxiosError<StorefrontApiError>
  requestId?: string
  isCancel: boolean
}

type ErrorAppearance = 'CUSTOM' | 'FULLPAGE' | 'MESSAGE' | 'NONE'

type ErrorHandlingConfig = {
  logError: boolean
  appearance: ErrorAppearance
  errorCode?: HttpStatusCode
  customMessage?: string
  customHandler?: string
  showRefreshButton: boolean
}

const logError = (error: ApiError): void => {
  if (error.axiosError) {
    if (error.axiosError.response?.data) {
      Sentry.setExtra('StorefrontApiError', error.axiosError.response.data)
    }
    if (error.requestId) {
      Sentry.setTag('requestId', error.requestId)
    }
    Sentry.captureException(error.axiosError)
    // eslint-disable-next-line no-console
    console.error(error.axiosError)
  }
}

const getGenericErrorMessage = (errorCode?: HttpStatusCode) => {
  switch (errorCode) {
    case 404:
      return t('error.pageNotFound')
    case 408:
      return t('error.timeout')
    case 409:
      return t('error.refresh')
    case 429:
      return t('error.tooManyRequests')
    case 500:
      return t('error.serverError')
    default:
      return t('error.unknownError', [errorCode])
  }
}

const handleApiError = (
  error: ApiError,
  defaultConfigParams?: Partial<ErrorHandlingConfig>,
  ...specificConfigParams: Array<Partial<ErrorHandlingConfig>>
): void => {
  // filter out cancel errors & don't log errors in redslave mode
  if (error.isCancel || useAuthentication().isRedslave()) {
    return
  }

  // server offline
  if (error.code === 503) {
    useRequestManager().setServerOffline(true)
    return
  }

  // logout user immediately on 401
  if (error.code === 401) {
    useAuthentication().logout()
    return
  }

  // merge config
  let defaultConfig: ErrorHandlingConfig = {
    logError: true,
    appearance: 'MESSAGE',
    errorCode: error.code,
    showRefreshButton: error.code == 409
  }
  defaultConfig = { ...defaultConfig, ...defaultConfigParams }

  let specificConfig: Partial<ErrorHandlingConfig> = {}

  specificConfigParams.forEach((specificConfigParam) => {
    if (specificConfigParam.errorCode && specificConfigParam.errorCode === error.code) {
      specificConfig = specificConfigParam
    }
  })

  const config: ErrorHandlingConfig = { ...defaultConfig, ...specificConfig }

  // don't log timeout, conflict and too many requests errors
  if (error.code === 408 || error.code === 409 || error.code === 429) {
    config.logError = false
  }

  // log error to sentry & console
  if (config.logError) {
    logError(error)
  }

  const errorMessage = getErrorMessage(config, error)

  showError(config, errorMessage, error.code, error.requestId)
}

// priority for error message
// 1. Custom message
// 2. Message from backend
// 3. Generic message
const getErrorMessage = (config: ErrorHandlingConfig, error: ApiError): string => {
  if (config.customMessage) {
    return config.customMessage
  }

  if (error.axiosError?.response?.data?.message) {
    return error.axiosError.response.data.message
  }

  return getGenericErrorMessage(config.errorCode)
}

const showError = (
  config: ErrorHandlingConfig,
  message: string,
  errorCode?: HttpStatusCode,
  errorId?: string
) => {
  if (config.appearance === 'FULLPAGE' || config.appearance === 'CUSTOM') {
    useError().setError({
      message,
      code: errorCode,
      errorId,
      customHandler:
        config.appearance === 'CUSTOM' && config.customHandler ? config.customHandler : undefined
    })
  } else if (config.appearance === 'MESSAGE') {
    if (config.showRefreshButton) {
      helpers.reportRefreshError(message)
    } else {
      helpers.reportError(message, errorId)
    }
  } else {
    return
  }
}

export { ApiError, ErrorHandlingConfig, handleApiError, StorefrontApiError }
